import { computed, inject, Injectable, InjectionToken, Signal, signal  } from '@angular/core';
import { Clerk } from '@clerk/clerk-js';
import { environment } from '../../environments/environment';
import { UserResource } from '@clerk/shared/types/user';
import { ui } from '@clerk/ui';

export const CLERK_FACTORY = new InjectionToken<(key: string) => Clerk>(
  'CLERK_FACTORY',
  { factory: () => (key: string) => new Clerk(key) },
);

@Injectable({ providedIn: 'root' })
export class ClerkAuthService {
  // Clerk est deja charge via provideAppInitializer au bootstrap
  private readonly createClerk = inject(CLERK_FACTORY);
  
  public currentUser = signal<UserResource | null>(null);
  public initialized = signal<boolean>(false);

  private clerk: Clerk | null = null;
  private initializationPromise: Promise<void> | null = null;

  public isLoggedIn: Signal<boolean> = computed(() => this.currentUser() !== null);

  async initialize(): Promise<void> {
    if (this.initialized()) {
      return;
    }

    if (this.initializationPromise) {
      return this.initializationPromise;
    }
    
    const clerk = this.createClerk(environment.clerkPublishableKey);;
    this.clerk = clerk;

    // Stocker la promesse d'initialisation
    this.initializationPromise = clerk.load({ui: ui})
      .then(async () => {
        this.currentUser.set(clerk.user ?? null);
        
        // Ecoute les changements futurs : connexion, deconnexion, etc.
        clerk.addListener(async (resources) => {
          this.currentUser.set(resources.user ?? null);
        });

        this.initialized.set(true);
      })
      .catch((error) => {
        this.initializationPromise = null;
        this.initialized.set(false);
        this.clerk = null;
        console.error('Failed to load Clerk : ', error);
        throw error;
      });
    
    return this.initializationPromise;
  }

  async signOut(): Promise<void> {
    await this.clerk?.signOut();
  }

  // On recupere un token frais a chaque requete (getToken() le renouvelle automatiquement si necessaire).
  // `template` permet d'obtenir un token issu d'un JWT template Clerk (claims et duree personnalises).
  getToken(template?: string): Promise<string | null> {
    const session = this.clerk?.session;

    if (!session) {
      return Promise.resolve(null);
    }

    return template ? session.getToken({ template }) : session.getToken();
  }

  getClerkInstance(): Clerk | null {
    return this.clerk;
  }
}
